<template>
    <div>
        <div class="w-full md:w-4/5 m-auto py-6 px-0 md:px-12">
            <div v-if="!loading" class="lds-spinner">
                <div /><div /><div /><div /><div /><div /><div /><div /><div /><div /><div /><div />
            </div>
            <h1 class="mb-4 text-green-600 text-2xl text-left font-bold text-2xl">
                <router-link :to="{ name: 'ViewApplication' }">Applications</router-link>
                / {{ personalData.first_name }} {{ personalData.last_name }}
            </h1>
            <div class="bg-white bg-gray-100 border px-2 py-2 mt-1 pb-6">
                <div class="flex justify-between mb-6 px-8 mt-4 align-baseline text-sm">
                    <div v-if="loading" class="w-full lg:flex text-left">
                        <div class="w-full flex lg:w-1/2">
                            <div>
                                <div>Applicant:</div>
                                <div>Programme:</div>
                                <div>Programme type:</div>
                            </div>
                            <div class="ml-4">
                                <div>{{ personalData.first_name }} {{ personalData.last_name }}</div>
                                <div v-if="applicationData.activeChoice.data?.programme_title">{{ applicationData.activeChoice.data?.programme_title }}</div><div v-else>&nbsp;</div>
                                <div v-if="firstChoiceType">{{ firstChoiceType.name }}</div><div v-else>&nbsp;</div>
                            </div>
                        </div>
                        <div class="w-full flex lg:w-1/2">
                            <div>
                                <div>Academic year:</div>
                                <div>Study mode:</div>
                                <div>Status:</div>
                            </div>
                            <div class="ml-4">
                                <div v-if="applicationData.activeChoice.data?.academic_year">{{ applicationData.activeChoice.data?.academic_year }}</div><div v-else>&nbsp;</div>
                                <div v-if="applicationData.activeChoice.data?.mode_of_study_name">{{ applicationData.activeChoice.data?.mode_of_study_name }}</div><div v-else>&nbsp;</div>
                                <div>{{ applicationData.public_status }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="loading">
                    <div id="references" class="bg-white border border-gray-300 flex justify-between px-8 mt-1">
                        <h1 class="text-1xl leading-10">Referees</h1>
                    </div>
                    <div id="references_content" class="bg-white border border-t-0 border-gray-300 overflow-hidden flex flex-wrap">
                        <p class="text-left mx-8 mt-4 font-bold">
                            You can change the referee(s) listed below. You are unable to change referee if they have already started writing your reference. You currently have {{ applicationData['referees']['data'].length }} referees assigned to your application
                        </p>

                        <div v-if="applicationData['referees']['data'].length < firstChoiceType['num_references_required']" class="m-4">
                            <p class="text-left m-4">You need to add another referee to your application</p>
                            <referee-form
                                :referee-data="newForm"
                                :application-reference="applicationData.reference"
                                :item-index="1"
                                :countries-of-domicile-list="countriesOfDomicileList"
                                :titles-list="titlesList"
                                :applicant-email="personalData?.email"
                                :hide-delete-btn="true"
                                @delete="removeOne"
                            />
                        </div>

                        <div v-if="successMessage" class="m-8 success-message w-full">{{ successMessage }}</div>

                        <div v-for="(referee, index) in applicationData['referees']['data']" :key="index" class="w-full m-8">
                            <div>
                                <h1 class="mb-4 text-green-600 text-2xl text-left font-bold text-2xl">Referee {{ index+1 }}</h1>
                                <h1 v-if="referee.references.data.known_years" class="text-left mb-2 font-bold">{{ referee.name }} has {{ referee.locked_in && referee.references.data.known_years ? 'submitted' : 'already started writing' }} your reference, so this referee cannot be changed</h1>
                                <h1 v-if="!referee.references.data.known_years" class="text-left mb-2 font-bold">You can delete this referee and add a new one</h1>
                                <table class="text-left bg-gray-50">
                                    <tbody>
                                        <tr>
                                            <th>Title:</th>
                                            <td>{{ referee.title }}</td>
                                        </tr>
                                        <tr>
                                            <th>Name:</th>
                                            <td>{{ referee.name }}</td>
                                        </tr>
                                        <tr>
                                            <th>Position:</th>
                                            <td>{{ referee.position }}</td>
                                        </tr>
                                        <tr>
                                            <th>Organisation:</th>
                                            <td>{{ referee.org }}</td>
                                        </tr>
                                        <tr>
                                            <th>Email:</th>
                                            <td>{{ referee.email }}</td>
                                        </tr>
                                        <tr>
                                            <th>Address Line 1:</th>
                                            <td>{{ referee.address_line1 }}</td>
                                        </tr>
                                        <tr>
                                            <th>Address Line 2:</th>
                                            <td>{{ referee.address_line2 }}</td>
                                        </tr>
                                        <tr>
                                            <th>Address Line 3:</th>
                                            <td>{{ referee.address_line3 }}</td>
                                        </tr>
                                        <tr>
                                            <th>Town / City:</th>
                                            <td>{{ referee.city_town }}</td>
                                        </tr>
                                        <tr>
                                            <th>County / State:</th>
                                            <td>{{ referee.county_state }}</td>
                                        </tr>
                                        <tr>
                                            <th>Post / Zip Code:</th>
                                            <td>{{ referee.post_zip_code }}</td>
                                        </tr>
                                        <tr>
                                            <th>Country:</th>
                                            <div v-for="country in countriesOfDomicileList" :key="country['reference']">
                                                <td v-if="referee.country_id == country.reference">{{ country.name }}</td>
                                            </div>
                                        </tr>
                                        <tr>
                                            <th>Phone Number:</th>
                                            <td>{{ referee.phone_number }}</td>
                                        </tr>
                                        <tr>
                                            <th>Reference Status:</th>
                                            <td class="text-red-500">{{ referee.locked_in && referee.references.data.known_years ? 'Submitted' : !referee.locked_in && referee.references.data.known_years ? 'In progress' : 'Not started' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <button v-if="!referee.references.data.known_years" class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 mt-4 rounded flex" type="button" @click="removeRecord(referee.reference)">
                                Replace this referee
                            </button>
                            <div v-if="!referee.references.data.known_years">
                                <button class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 mt-4 rounded flex" type="button" @click="emailReferee(referee.reference)">
                                    Send another reference request email
                                </button> <p class="text-left pt-1">An email will only be sent if more than 7 days have elapsed since the last email sent to the recipient regarding references for this application</p>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// import Referees from './Details/Referees'
import RefereeForm from '@/views/Application/Details/RefereeForm'

export default {
    name: 'EditReferees',
    metaInfo() {
        return {
            title: 'Edit Referees',
        }
    },
    components: {
        // Referees,
        RefereeForm,
    },
    data() {
        return {
            applicationData: [],
            personalData: {
                first_name : '',
                last_name : '',
            },
            newForm: {
                reference: null,
                title: null,
                name: null,
                position: null,
                org: null,
                email: null,
                address_line1: null,
                address_line2: null,
                address_line3: null,
                city_town: null,
                county_state: null,
                post_zip_code: null,
                country_id: null,
                phone_number: null,
                fax_number: null,
                application_id: null,
            },
            loading: false,
            firstChoiceTypeReference: '',
            firstChoiceReference: '',
            successMessage: '',
        }
    },
    computed: {
        firstChoiceType: {
            get: function() {
                const programmeTypes = this.$store.getters.programmeTypesList

                if (programmeTypes !== undefined)
                {
                    if (this.firstChoiceTypeReference !== undefined && this.firstChoiceTypeReference.length > 0)
                    {
                        return programmeTypes.data.filter(programmeType => programmeType['reference'] === this.firstChoiceTypeReference)[0]
                    }
                    else
                    {
                        if (programmeTypes.data.length > 0 && this.$store.getters.applicationDetails['data']['first_choice_type'])
                        {
                            return programmeTypes.data.filter(programmeType => programmeType['reference'] === this.$store.getters.applicationDetails['data']['first_choice_type'])[0]
                        }
                    }
                }
                return {name: '', num_references_required : 2}
            },
            set: function(newValue) {
                this.firstChoiceTypeReference = newValue
            },
        },
        firstChoice: {
            get: function() {
                const programmes = this.$store.getters.programmesList

                if (programmes !== undefined)
                {
                    if (this.firstChoiceReference !== undefined && this.firstChoiceReference.length > 0)
                    {
                        return programmes.data.filter(programme => programme['reference'] === this.firstChoiceReference)[0]
                    }
                    else
                    {
                        if (programmes.data.length > 0 && this.$store.getters.applicationDetails['data']['first_choice'] !== null)
                        {
                            return programmes.data.filter(programme => programme['reference'] === this.$store.getters.applicationDetails['data']['first_choice'])[0]
                        }
                    }
                }
                return {name : '', programme_title: '', sits_ipo_ayrc: '', moa_name: ''}
            },
            set: function(newValue) {
                this.firstChoiceReference = newValue
            },
        },
        applicationDetails() {
            return this.$store.getters.applicationDetails
        },
        programmesList() {
            return this.$store.getters.programmesList
        },
        titlesList() {
            return this.$store.getters.titlesList
        },
        countriesOfDomicileList() {
            return this.$store.getters.countriesOfDomicileList
        },
    },
    watch: {
        applicationDetails(data) {
            this.applicationData = data.data
            this.personalData = data.data.user.data
            this.loading = true
            this.newForm.application_id = this.applicationData.reference
        },
        programmesList(data) {
            this.programmes = data
        },
        gendersList(data) {
            this.genders = data
        },
    },
    created() {
        this.loadApplication()
    },
    methods: {
        loadApplication() {
            this.$store.dispatch('retrieveSingleApplication', {
                applicationRef: this.$route.params.id,
            })
        },
        removeRecord(refereeRef) {
            if(confirm('Are you sure you want to replace this referee with a new one?'))
            {
                this.loading = false

                this.$store.dispatch('deleteReferee', {
                    applicationReference: this.applicationData.reference,
                    refereeReference: refereeRef,
                }).then(response => {
                    this.loading = true
                    this.successMessage = response.data.message
                    this.loadApplication()
                })
                .catch(() => {
                    this.loading = true
                    this.successMessage = ''
                })
            }
        },
        emailReferee(refereeRef) {
            this.loading = false

            this.$store.dispatch('emailReferee', {
                applicationReference: this.applicationData.reference,
                refereeReference: refereeRef,
            }).then(response => {
                this.loading = true
                this.successMessage = response.data.message
                this.loadApplication()
            })
                .catch(() => {
                    this.loading = true
                    this.successMessage = ''
                })
        },
    },
}
</script>
<style scoped>
    table {
        width: 100%;
        border:1px solid #eee;
    }
    table tr {
        border:1px solid #eee;
    }
    table td {
        padding:5px;
        border:1px solid #eee;
    }
    table th {
        padding:5px;
        width:50%;
    }
    @media screen and (min-width: 1201px) {
        table th {
            width:25%;
        }
    }
</style>